import axios from 'axios'
import { auth } from './firebase'

const REPORTS_HOST = `https://us-central1-${process.env.VUE_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/generateWorkOrderReport`

const reportsClient = axios.create({
    baseURL: REPORTS_HOST,
    timeout: 11 * 60 * 1000,
})

reportsClient.interceptors.request.use(async config => {
    const user = auth().currentUser
    if (user) {
        const token = await user.getIdToken()
        config.headers.Authorization = `Bearer ${token}`
    }
    return config
})

function buildReportErrorMessage(error) {
    const status = error.response?.status
    const errorResponse = error.response?.data
    if (errorResponse) {
        const serverMessage =
            errorResponse.message || errorResponse.error || error.message
        if (status === 400) {
            return serverMessage || 'Invalid report request'
        }
        if (status === 401 || status === 403) {
            return serverMessage || 'Authentication failed. Please sign in again.'
        }
        if (status === 504 || status === 408) {
            return (
                serverMessage ||
                'Report generation timed out. Please try a shorter date range.'
            )
        }
        if (status >= 500) {
            return (
                serverMessage ||
                `Report server error (${status}). Please try again; if it persists, use a shorter date range.`
            )
        }
        return serverMessage
    }
    if (error.code === 'ECONNABORTED') {
        return 'Report generation timed out. Please try a shorter date range.'
    }
    if (error.request) {
        return 'Could not reach the report server (network error or timeout). Please try again; if the problem persists, try a shorter date range.'
    }
    return error.message
}

export const REPORT_SLUG_BY_TYPE = {
    'By Status': 'by-status',
    'Produced KG Per Process': 'produced-kg-per-process',
    'Per Product Type': 'per-product-type',
    'Per Product': 'per-product',
    'Shipped Per Month': 'shipped-per-month',
    'Per Process': 'per-process',
    'Entered, Produced and Accumulated Per Process':
        'entered-produced-accumulated-per-process',
    'Entered, Produced and Accumulated All Processes':
        'entered-produced-accumulated-all-processes',
    Rejection: 'rejection',
    'Rejected Work Orders': 'rejected-work-orders',
    'History of Generated WO': 'history-of-generated-wo',
}

/**
 * Generate a work-order production report via Cloud Function.
 * Returns { downloadUrl, fileName, expiresAt }.
 */
export async function generateWorkOrderReport(reportType, payload) {
    const slug = REPORT_SLUG_BY_TYPE[reportType]
    if (!slug) {
        throw new Error(`Unsupported report type: ${reportType}`)
    }
    try {
        const { data } = await reportsClient.post(
            `/reports/work-orders/${slug}`,
            payload
        )
        if (!data?.success || !data?.data?.downloadUrl) {
            throw new Error(data?.message || 'Report generation failed')
        }
        return data.data
    } catch (error) {
        throw new Error(buildReportErrorMessage(error))
    }
}

export default { generateWorkOrderReport, REPORT_SLUG_BY_TYPE }
